// src/app/core/services/navigation.service.ts
import { Injectable } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { NavigationData, MenuItem, MainMenuItem, FooterSection, SocialLink, MenuSection } from '../models/navigation.types';
import { navigationData } from '../data/navigation.data';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  private readonly navigationData: NavigationData = navigationData;

  constructor(
    private title: Title,
    private meta: Meta,
    private router: Router
  ) {
    // Listen to route changes to update meta tags
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.updateMetaTags(event.url);
    });
  }

  getMainMenu(): MainMenuItem[] {
    return this.navigationData.mainMenu;
  }

  getFooterSections(): FooterSection[] {
    return this.navigationData.footerSections;
  }

  getSocialLinks(): SocialLink[] {
    return this.navigationData.socialLinks;
  }

  getLegalLinks(): MenuItem[] {
    return this.navigationData.legalLinks;
  }

  getAdditionalRoutes(): MenuItem[] {
    return this.navigationData.additionalRoutes;
  }

  getAllRoutes(): MenuItem[] {
    const routes = new Set<MenuItem>();
    
    // Collect main menu routes
    this.navigationData.mainMenu.forEach(menu => {
      menu.sections.forEach(section => {
        section.items.forEach(item => routes.add(item));
      });
    });

    // Collect footer routes
    this.navigationData.footerSections.forEach(section => {
      section.links.forEach(link => routes.add(link));
    });

    // Add additional routes
    this.navigationData.additionalRoutes.forEach(route => routes.add(route));

    // Add legal routes
    this.navigationData.legalLinks.forEach(link => routes.add(link));

    return Array.from(routes);
  }

  getRouteByPath(path: string): MenuItem | undefined {
    return this.getAllRoutes().find(route => route.route === path);
  }

  updateMetaTags(currentRoute: string): void {
    const routeData = this.getRouteByPath(currentRoute);
    
    if (routeData) {
      // Update title
      this.title.setTitle(routeData.pageTitle || `${routeData.title} - Industry Intelligence`);

      // Update meta description
      if (routeData.pageDescription) {
        this.meta.updateTag({ 
          name: 'description', 
          content: routeData.pageDescription 
        });
      }

      // Update Open Graph tags
      this.meta.updateTag({
        property: 'og:title',
        content: routeData.pageTitle || routeData.title
      });

      if (routeData.pageDescription) {
        this.meta.updateTag({
          property: 'og:description',
          content: routeData.pageDescription
        });
      }

      this.meta.updateTag({
        property: 'og:url',
        content: `https://yourdomain.com${currentRoute}`
      });
    }
  }

  getBreadcrumbs(currentRoute: string): MenuItem[] {
    const parts = currentRoute.split('/').filter(part => part);
    const breadcrumbs: MenuItem[] = [];
    let currentPath = '';

    parts.forEach(part => {
      currentPath += `/${part}`;
      const routeData = this.getRouteByPath(currentPath);
      if (routeData) {
        breadcrumbs.push(routeData);
      }
    });

    return breadcrumbs;
  }

  getRelatedRoutes(currentRoute: string, limit: number = 3): MenuItem[] {
    const currentRouteData = this.getRouteByPath(currentRoute);
    if (!currentRouteData) return [];

    // Find the section this route belongs to
    let parentSection: MenuSection | undefined;
    this.navigationData.mainMenu.forEach(menu => {
      menu.sections.forEach(section => {
        if (section.items.some(item => item.route === currentRoute)) {
          parentSection = section;
        }
      });
    });

    if (!parentSection) return [];

    // Get other items from the same section, excluding the current route
    return parentSection.items
      .filter((item: MenuItem) => item.route !== currentRoute)
      .slice(0, limit);
  }
}